import * as React from 'react';
import type { Maybe, AdrollSettings } from '@types/gatsby-types';
import { graphql, useStaticQuery } from 'gatsby';
import { isAutomatedTest } from 'core/lib/isAutomatedTest';
import { IS_BROWSER } from 'core/constants';

const query = graphql`
    query YelpSettingsQuery {
        settings {
            adroll {
                pixelId
                clickToCallSegment
                getDirectionsSegment
                scheduleAppointmentSegment
            }
        }
    }
`;

type YelpSettingsQuery = {
    settings: Maybe<
        Pick<
            AdrollSettings,
            | 'pixelId'
            | 'clickToCallSegment'
            | 'getDirectionsSegment'
            | 'scheduleAppointmentSegment'
        >
    >;
};

type TrackWithYelp = (segmentName: string) => void;

type Track = () => void;

type UseYelpTracking = {
    trackWithYelp: TrackWithYelp;
    trackClickToCall: Track;
    trackGetDirections: Track;
    trackScheduleAppointment: Track;
};

export default function useYelpTracking(): UseYelpTracking {
    const { settings } = useStaticQuery<YelpSettingsQuery>(query);
    const isRunningInAutomatedTest = isAutomatedTest();
    const trackWithYelp = React.useCallback(
        (segmentName: string) => {
            if (isRunningInAutomatedTest) {
                // eslint-disable-next-line no-console
                console.log(
                    'automated test detected – skipping Yelp conversion tracking',
                );
            } else {
                const pixelId = settings?.adroll?.pixelId;

                if (
                    IS_BROWSER &&
                    pixelId &&
                    window.adroll &&
                    window.adroll.track &&
                    typeof window.adroll.track === 'function'
                ) {
                    window.adroll.track('pageView', {
                        segment_name: segmentName,
                    });
                }
            }
        },
        [settings?.adroll?.pixelId, isRunningInAutomatedTest],
    );

    const trackClickToCall = React.useCallback(() => {
        if (settings?.adroll?.clickToCallSegment) {
            trackWithYelp(settings.adroll.clickToCallSegment);
        }
    }, [trackWithYelp, settings?.adroll?.clickToCallSegment]);

    const trackGetDirections = React.useCallback(() => {
        if (settings?.adroll?.getDirectionsSegment) {
            trackWithYelp(settings.adroll.getDirectionsSegment);
        }
    }, [trackWithYelp, settings?.adroll?.getDirectionsSegment]);

    const trackScheduleAppointment = React.useCallback(() => {
        if (settings?.adroll?.scheduleAppointmentSegment) {
            trackWithYelp(settings.adroll.scheduleAppointmentSegment);
        }
    }, [trackWithYelp, settings?.adroll?.scheduleAppointmentSegment]);

    return {
        trackClickToCall,
        trackGetDirections,
        trackScheduleAppointment,
        trackWithYelp,
    };
}
